import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { RemoveButton } from '@app/components/RemoveButton';
import { FlagLevelIcon } from '@app/components/FlagLevelIcon';
import { useEntity } from '@app/hooks/useEntity';
import { FlagDefinition, FlagLevel, getFlagLevelConfig } from '@app/model/Flag';
import { useFlagDefinitionStore } from '@app/store/entityStore';
import { ActionList, ActionListItem, AlertVariant, Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, ModalVariant, PageSection, Pagination, PaginationVariant, SearchInput, Split, SplitItem, Switch, Title, Toolbar, ToolbarContent, ToolbarItem, Tooltip } from '@patternfly/react-core';
import { PencilAltIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { DataLoadErrorRow } from '@app/components/DataLoadError';

export const FlagsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  
  // --- Cache Connection ---
  const { items: flags, error, retry } = useEntity(useFlagDefinitionStore);
  const saveFlagDef = useFlagDefinitionStore(state => state.save);
  const deleteFlagDef = useFlagDefinitionStore(state => state.delete);

  const { perPage, setPerPage } = useTablePreferences('flags', 10);

  const { restoredPage, rememberRow, rowProps } = useRowRestore('flags');
  const [page, setPage] = React.useState(restoredPage ?? 1);
  // The search last typed here, kept only for the trip out to a rule and back.
  const { restored, keep } = useFilterRestore<{ search: string }>('flags');
  const [search, setSearch] = React.useState(restored?.search ?? '');

  React.useEffect(() => { keep({ search }); }, [search, keep]);

  // NEW: State for bulk selection
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  
  // NEW: State for Status Confirmation Modal
  const [statusModal, setStatusModal] = React.useState<{ isOpen: boolean; targetFlags: FlagDefinition[]; targetStatus: boolean }>({
    isOpen: false, targetFlags: [], targetStatus: true
  });

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo(
    () => flags.filter(f => f.name.toLowerCase().includes(search.toLowerCase())),
    [flags, search]
  );

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  // --- Selection Handlers ---
  const onSelectRow = (flag: FlagDefinition, isSelecting: boolean) => {
    if (!flag.id) return;
    const newSelected = new Set(selectedIds);
    if (isSelecting) {
      newSelected.add(flag.id);
    } else {
      newSelected.delete(flag.id);
    }
    setSelectedIds(newSelected);
  };

  // Selects every rule the search matches, not just the page on screen - the bulk
  // enable/disable buttons have always acted on the whole list, and narrowing them
  // to the visible page would quietly change what those buttons do.
  const onSelectAll = (isSelecting: boolean) => {
    if (isSelecting) {
      setSelectedIds(new Set(filteredRows.map(f => f.id).filter((id): id is number => id !== undefined)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const getSelectedFlags = () => flags.filter(f => f.id && selectedIds.has(f.id));

  // --- Status Change Handlers ---
  const executeStatusChange = async () => {
    beginLoading();
    try {
      // Create an array of store updates for all targeted flags
      const updatePromises = statusModal.targetFlags.map(flag => {
        const updatedFlag = { ...flag, enabled: statusModal.targetStatus };
        // The store handles the PUT request and the local cache update automatically
        return saveFlagDef(updatedFlag, false);
      });
      
      await Promise.all(updatePromises);
      addNotification(`Successfully ${statusModal.targetStatus ? 'enabled' : 'disabled'} ${statusModal.targetFlags.length} rule(s).`, AlertVariant.success);
      
      setSelectedIds(new Set()); // Clear selection
      setStatusModal({ isOpen: false, targetFlags: [], targetStatus: true });
      
      // We deleted loadFlags() here because the UI is already perfectly up to date!
    } catch (err: any) {
      addNotification(`Error updating status: ${err.message}`, AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onRemove = async (flag: FlagDefinition) => {
    if (!flag.id) return;
    beginLoading();
    try {
      await deleteFlagDef(flag.id);
      addNotification(`Rule "${flag.name}" deleted successfully.`, AlertVariant.info);
    } catch (error: any) {
      addNotification(`Error deleting rule: ${error.message}`, AlertVariant.danger);
    } finally {
      endLoading();
    }
  }

  const renderLevelLabel = (level: string) => {
    const config = getFlagLevelConfig(level as FlagLevel);
    if (!config) {
      return <Label color="grey">{level}</Label>;
    }
    return (
      <Label color={config.color} icon={<FlagLevelIcon level={level as FlagLevel} />}>
        {config.text}
      </Label>
    );
  };

  // --- UI Helpers ---
  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact={variant === 'top'}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  return (
    <React.Fragment>
      {/* Header Section */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl" style={{ marginBottom: '1rem' }}>Rule Engine</Title>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Tooltip content="New Flag">
                  <Button variant="primary" onClick={() => navigate('/admin/flags/new')}>New Flag</Button>
                </Tooltip>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>
      <PageSection>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              {/* Back to page 1 on every search, or a narrowed result set can leave
                  the user stranded on a page that no longer exists. */}
              <SearchInput value={search} placeholder="Search rules..."
                onChange={(_e, v) => { setSearch(v); setPage(1); }}
                onClear={() => { setSearch(''); setPage(1); }} />
            </ToolbarItem>

            <ToolbarItem>
              <Button 
                variant="secondary" 
                isDisabled={selectedIds.size === 0}
                onClick={() => setStatusModal({ isOpen: true, targetFlags: getSelectedFlags(), targetStatus: true })}
              >
                Enable Selected
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button 
                variant="secondary" 
                isDisabled={selectedIds.size === 0}
                onClick={() => setStatusModal({ isOpen: true, targetFlags: getSelectedFlags(), targetStatus: false })}
              >
                Disable Selected
              </Button>
            </ToolbarItem>
            <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {/* Frozen Actions column: the scroll container is the horizontal scroll it sticks to, and
            gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
        <InnerScrollContainer>
          <Table variant="compact" gridBreakPoint="">
            <Thead>
              <Tr>
                <Th aria-label='Select' select={{
                  onSelect: (_evt, isSelecting) => onSelectAll(isSelecting),
                  isSelected: filteredRows.length > 0 && filteredRows.every(f => f.id !== undefined && selectedIds.has(f.id))
                }} />
                <Th>Name</Th>
                <Th>Target</Th>
                <Th>Level</Th>
                <Th>Status</Th>
                <Th isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedRows.map((flag, rowIndex) => (
                <Tr key={flag.id} {...rowProps(flag.id)}>
                  <Td select={{
                    rowIndex,
                    onSelect: (_evt, isSelecting) => onSelectRow(flag, isSelecting),
                    isSelected: flag.id !== undefined && selectedIds.has(flag.id)
                  }} />
                  <Td dataLabel="Name">{flag.name}</Td>
                  <Td dataLabel="Target">{flag.targetEntity}</Td>
                  <Td dataLabel="Level">{renderLevelLabel(flag.level)}</Td>
                
                  <Td dataLabel="Status">
                    <Switch 
                      id={`status-switch-${flag.id}`}
                      isChecked={flag.enabled} 
                      label="Enabled" 
                      onChange={(_e, isChecked) => setStatusModal({ isOpen: true, targetFlags: [flag], targetStatus: isChecked })} 
                    />
                  </Td>
                
                  <Td dataLabel="Actions"
                
                    isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                    <ActionList>
                      <ActionListItem>
                        <Button variant="plain" onClick={() => { rememberRow(flag.id, page); navigate(`/admin/flags/${flag.id}`); }} icon={<PencilAltIcon />} />
                      </ActionListItem>
                      <ActionListItem>
                        <RemoveButton 
                          confirmationMessage={`Delete rule "${flag.name}"?`} 
                          onRemoveConfirm={() => onRemove(flag)} 
                        />
                      </ActionListItem>
                    </ActionList>
                  </Td>
                </Tr>
              ))}
              {/* Nothing to show because the load failed, rather than because the list is empty */}
              {error && filteredRows.length === 0 && (
                <DataLoadErrorRow colSpan={6} error={error} entityLabel="flag definitions" onRetry={retry} />
              )}
            </Tbody>
          </Table>
        </InnerScrollContainer>
        {buildPagination('bottom')}
      </PageSection>

      {/* NEW: The Status Change Confirmation Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal({ ...statusModal, isOpen: false })}
        aria-labelledby="status-modal-title"
      >
        <ModalHeader 
          title="Confirm Status Change" 
          titleIconVariant={statusModal.targetStatus ? 'info' : 'warning'} 
          labelId="status-modal-title" 
        />
        <ModalBody>
          <p>
            Are you sure you want to <strong>{statusModal.targetStatus ? 'Enable' : 'Disable'}</strong> the {statusModal.targetFlags.length} selected rule(s)?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button 
            key="confirm" 
            variant="primary" 
            onClick={executeStatusChange}
          >
            Confirm
          </Button>
          <Button 
            key="cancel" 
            variant="link" 
            onClick={() => setStatusModal({ ...statusModal, isOpen: false })}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};